import type {
  CategorySpending,
  MerchantSpending,
  PaymentSpending,
  PaymentWithoutForecast,
  SpendingMonth,
  SummaryBank,
  YearSummary,
} from '../../model/year-summary.js';
import type {
  CategorySpendingDto,
  MerchantSpendingDto,
  PaymentSpendingDto,
  SpendingMonthDto,
  SummaryBankDto,
  YearSummaryDto,
} from '../dto/year-summary.dto.js';

type PaymentWithoutForecastDto = YearSummaryDto['withoutForecast'][number];

const toSpendingMonth = (dto: SpendingMonthDto): SpendingMonth => ({
  month: dto.month,
  exactMinor: dto.exactMinor,
  estimatedMinor: dto.estimatedMinor,
});

const toCategorySpending = (dto: CategorySpendingDto): CategorySpending => ({
  categoryId: dto.categoryId,
  annualCostMinor: dto.annualCostMinor,
  exactMinor: dto.exactMinor,
  estimatedMinor: dto.estimatedMinor,
  bankMinor: dto.bankMinor,
});

const toPaymentSpending = (dto: PaymentSpendingDto): PaymentSpending => ({
  paymentId: dto.paymentId,
  name: dto.name,
  categoryId: dto.categoryId,
  annualCostMinor: dto.annualCostMinor,
  exactMinor: dto.exactMinor,
  estimatedMinor: dto.estimatedMinor,
  isConverted: dto.isConverted,
});

const toMerchantSpending = (dto: MerchantSpendingDto): MerchantSpending => ({
  categoryId: dto.categoryId,
  merchantKey: dto.merchantKey,
  name: dto.name,
  spentMinor: dto.spentMinor,
  operationCount: dto.operationCount,
});

const toPaymentWithoutForecast = (dto: PaymentWithoutForecastDto): PaymentWithoutForecast => ({
  paymentId: dto.paymentId,
  name: dto.name,
});

const toSummaryBank = (dto: SummaryBankDto | null): SummaryBank | null => {
  if (dto === null) {
    return null;
  }

  return {
    isHistoryComplete: dto.isHistoryComplete,
    convertedOperationCount: dto.convertedOperationCount,
    unconvertedOperationCount: dto.unconvertedOperationCount,
  };
};

export const toYearSummary = (dto: YearSummaryDto): YearSummary => ({
  year: dto.year,
  month: dto.month,
  baseCurrency: dto.baseCurrency,
  rateDate: dto.rateDate,
  annualCost: {
    totalMinor: dto.annualCost.totalMinor,
    isConverted: dto.annualCost.isConverted,
    hasEstimates: dto.annualCost.hasEstimates,
    paymentCount: dto.annualCost.paymentCount,
  },
  spending: {
    totalMinor: dto.spending.totalMinor,
    spentMinor: dto.spending.spentMinor,
    plannedMinor: dto.spending.plannedMinor,
    hasEstimates: dto.spending.hasEstimates,
    months: dto.spending.months.map(toSpendingMonth),
  },
  subscriptions: {
    exactMinor: dto.subscriptions.exactMinor,
    estimatedMinor: dto.subscriptions.estimatedMinor,
    isConverted: dto.subscriptions.isConverted,
  },
  income: {
    totalMinor: dto.income.totalMinor,
    isConverted: dto.income.isConverted,
  },
  categories: dto.categories.map(toCategorySpending),
  payments: dto.payments.map(toPaymentSpending),
  merchants: dto.merchants.map(toMerchantSpending),
  withoutForecast: dto.withoutForecast.map(toPaymentWithoutForecast),
  missingRates: dto.missingRates,
  unconvertedPaidCount: dto.unconvertedPaidCount,
  bank: toSummaryBank(dto.bank),
});
